import React, { useCallback } from 'react';
import { fetchWithdrawalRequests, getBuyRMBTransactions } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { Box, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton, Tooltip, Typography, Badge } from '@mui/material';
import { Dashboard, Inventory, ShoppingCart, Assessment, Settings, People, ChevronLeft, ChevronRight, Security, ExpandLess, ExpandMore, Group, AssignmentInd, AssignmentTurnedIn, ListAlt, Public, Map, LocationCity, Place, AttachMoney, AllInbox, MoneyOff, HourglassEmpty, Warehouse, LocalShipping, Flag, CheckCircle, Replay, Cancel, DoneAll, Translate, History, Api, Timeline, Analytics, Key, Payment, AccountBalance, TrendingUp, TrendingDown, SwapHoriz, CardGiftcard, Receipt, Business, AccountBalanceWallet, Message, SystemUpdate, Category, Schema, BrandingWatermark, RequestPage, GavelOutlined, VpnKey, Psychology, TrendingUp as TrendingUpIcon, Store, Inventory2, LocalShipping as LocalShippingIcon, Collections, AddShoppingCart, Handshake, AccountBalance as AccountBalanceIcon, CurrencyExchange, History as HistoryIcon, ShowChart, Campaign, Assignment, BarChart, AccountTree, AttachMoney as AttachMoneyIcon, BusinessCenter, Work, Badge as BadgeIcon, CalendarToday, Description, Folder, CreditCard, LocalAtm, Calculate, Storage, AddCircle, Groups, PlayArrow, Stop, RateReview, Devices } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOrderStatusCounts } from '../../store/slices/orderSlice';
import Skeleton from '@mui/material/Skeleton';
import { getRoutePermissions } from '../../routes/routePermissions';
import { hasAnyEndpointAccess } from '../../utils/rbac';
import { getOpenDisputesCount } from '../../services/package';
import { useSupportChatNotifications } from '../../context/SupportChatNotificationContext';
import { getPendingSupportReviewsCount } from '../../services/api';

const TRACKED_STATUS_PATHS = {
  '/admin/orders/unpaid': 'unpaid',
  '/admin/orders/processing': 'processing',
  '/admin/orders/purchased': 'purchased',
  '/admin/orders/in-warehouse': 'warehouse',
  '/admin/orders/shipped-out': 'shipped',
  '/admin/orders/in-nigeria': 'nigeria',
};



const getMenuItems = (t) => [
  { text: t('sidebar:menu.dashboard'), icon: <Dashboard />, path: '/admin/dashboard' },
  { text: t('sidebar:menu.items'), icon: <Inventory />, path: '/admin/items' },
  { text: 'Purchasing References', icon: <ListAlt />, path: '/admin/orders/purchasing-references' },
  { text: t('sidebar:menu.cart'), icon: <ShoppingCart />, path: '/admin/cart' },
  { text: t('sidebar:menu.users'), icon: <People />, path: '/admin/users' },
  { text: 'Device Info', icon: <Devices />, path: '/admin/device-info' },
  { text: 'Product Request', icon: <RequestPage />, path: '/admin/product-requests' },
  { text: t('sidebar:menu.invoice'), icon: <Receipt />, path: '/admin/invoice-management' },
  { text: t('sidebar:menu.systemMessages'), icon: <Message />, path: '/admin/system-messages' },
  { text: t('sidebar:menu.banners'), icon: <Collections />, path: '/admin/banners' },
  { text: t('sidebar:menu.manualItemAdd'), icon: <AddShoppingCart />, path: '/admin/manual-item-add' },
  { text: t('sidebar:menu.localSupplierItems'), icon: <Store />, path: '/admin/local-supplier-items' },
  { text: t('sidebar:menu.versionControl'), icon: <SystemUpdate />, path: '/admin/version-management' },
  { text: t('sidebar:menu.currencyRates'), icon: <AttachMoney />, path: '/admin/currency-rates' },
  { text: t('sidebar:menu.platformTranslations', 'Translation Management'), icon: <Translate />, path: '/admin/platform-translations' },
  { text: t('sidebar:menu.settings'), icon: <Settings />, path: '/admin/settings' },
];

const getRbacMenu = (t) => ({
  text: t('sidebar:menu.rbac'),
  icon: <Security />,
  submenus: [
    { text: t('sidebar:menu.accessControl'), icon: <Security />, path: '/admin/rbac/access-control' },
    { text: t('sidebar:menu.roleManagement'), icon: <Group />, path: '/admin/rbac/roles' },
  ],
});

const getWalletMenu = (t) => ({
  text: t('sidebar:menu.walletManagement'),
  icon: <AccountBalance />,
  path: '/admin/wallet-management',
});

const getWalletsMenu = (t) => ({
  text: t('sidebar:menu.wallets'),
  icon: <AccountBalanceWallet />,
  path: '/admin/wallets',
});

const getBonusManagementMenu = (t) => ({
  text: t('sidebar:menu.bonusManagement', 'Bonus Management'),
  icon: <CardGiftcard />,
  path: '/admin/bonus-management',
});

const getSupportChatMenu = () => ({
  text: 'Support Chat',
  icon: <Message />,
  path: '/admin/support-chat',
});

const getSupportAgentRatingsMenu = () => ({
  text: 'Agent Ratings',
  icon: <RateReview />,
  path: '/admin/support-chat/agent-ratings',
});

const getSupportKnowledgeMenu = () => ({
  text: 'Support Knowledge',
  icon: <Schema />,
  path: '/admin/support-chat/knowledge',
});

const getReviewsMenu = () => ({
  text: 'Customer Reviews',
  icon: <RateReview />,
  path: '/admin/reviews',
});

const getPaymentMethodsMenu = (t) => ({
  text: t('sidebar:menu.paymentManagement'),
  icon: <Payment />,
  submenus: [
    { text: t('sidebar:menu.paymentMethods'), icon: <Payment />, path: '/admin/payment-methods' },
    { text: t('sidebar:menu.paymentDetails'), icon: <AccountBalance />, path: '/admin/payment-details' },
  ],
});

const getWithdrawalsMenu = (t) => ({
  text: t('sidebar:menu.withdrawals'),
  icon: <MoneyOff />,
  path: '/admin/withdrawals',
});

const getLocationMenu = (t) => ({
  text: t('sidebar:menu.location'),
  icon: <Place />,
  submenus: [
    { text: t('sidebar:menu.country'), icon: <Public />, path: '/admin/location/country' },
    { text: t('sidebar:menu.state'), icon: <Map />, path: '/admin/location/state' },
    { text: t('sidebar:menu.city'), icon: <LocationCity />, path: '/admin/location/city' },
  ]
});

const getSearchMenu = (t) => ({
  text: t('sidebar:menu.search'),
  icon: <Translate />,
  submenus: [
    { text: t('sidebar:menu.translateSettings'), icon: <Translate />, path: '/admin/translate-settings' },
    { text: t('sidebar:menu.platformTranslations', 'Translation Management'), icon: <Translate />, path: '/admin/platform-translations' },
    { text: t('sidebar:menu.searchHistory'), icon: <History />, path: '/admin/search-history' },
  ]
});

const getApiMenu = (t) => ({
  text: t('sidebar:menu.api'),
  icon: <Api />,
  submenus: [
    { text: t('sidebar:menu.apiManagement'), icon: <Api />, path: '/admin/api-management' },
    { text: t('sidebar:menu.apiKeyManagement'), icon: <Key />, path: '/admin/api-key-management' },
    { text: t('sidebar:menu.apiUsageLogs'), icon: <Timeline />, path: '/admin/api-usage-logs' },
    { text: t('sidebar:menu.apiStatistics'), icon: <Analytics />, path: '/admin/api-statistics' },
  ]
});

const getOrdersMenu = (t) => ({
  text: t('sidebar:menu.orders'),
  icon: <ShoppingCart />,
  submenus: [
    { text: t('sidebar:menu.all'), icon: <AllInbox />, path: '/admin/orders/all' },
    { text: t('sidebar:menu.pendingApproval'), icon: <HourglassEmpty />, path: '/admin/orders/pending-approval' },
    { text: t('sidebar:menu.unpaid'), icon: <MoneyOff />, path: '/admin/orders/unpaid' },
    { text: t('sidebar:menu.processing'), icon: <HourglassEmpty />, path: '/admin/orders/processing' },
    { text: t('sidebar:menu.purchased'), icon: <ShoppingCart />, path: '/admin/orders/purchased' },
    { text: t('sidebar:menu.inWarehouse'), icon: <Warehouse />, path: '/admin/orders/in-warehouse' },
    { text: t('sidebar:menu.shippedOut'), icon: <LocalShipping />, path: '/admin/orders/shipped-out' },
    { text: t('sidebar:menu.inNigeria'), icon: <Flag />, path: '/admin/orders/in-nigeria' },
    { text: t('sidebar:menu.received'), icon: <CheckCircle />, path: '/admin/orders/receieved' },
    { text: t('sidebar:menu.refunded'), icon: <Replay />, path: '/admin/orders/refunded' },
    { text: t('sidebar:menu.success'), icon: <DoneAll />, path: '/admin/orders/success' },
    { text: t('sidebar:menu.closed'), icon: <Cancel />, path: '/admin/orders/closed' },
  ]
});

const getPreOrderMenu = (t) => ({
  text: t('sidebar:menu.preOrder', 'Pre-Order'),
  icon: <Inventory2 />,
  submenus: [
    { text: t('sidebar:menu.preOrderCreate', 'Create Pre-order'), icon: <AddCircle />, path: '/admin/pre-order/create' },
    { text: t('sidebar:menu.preOrderManage', 'Manage Pre-orders'), icon: <Inventory2 />, path: '/admin/pre-order/manage' },
  ],
});

const getTransactionsMenu = (t) => ({
  text: t('sidebar:menu.transactions'),
  icon: <AccountBalanceWallet />,
  submenus: [
    { text: t('sidebar:menu.all'), icon: <AllInbox />, path: '/admin/transactions' },
    { text: t('sidebar:menu.topUp'), icon: <TrendingUp />, path: '/admin/transactions/topup' },
    { text: t('sidebar:menu.withdraw'), icon: <TrendingDown />, path: '/admin/transactions/withdraw' },
    { text: t('sidebar:menu.refund'), icon: <Replay />, path: '/admin/transactions/refund' },
    { text: t('sidebar:menu.business'), icon: <Business />, path: '/admin/transactions/business' },
    { text: t('sidebar:menu.income'), icon: <TrendingUp />, path: '/admin/transactions/income' },
    { text: t('sidebar:menu.transfer'), icon: <SwapHoriz />, path: '/admin/transactions/transfer' },
    { text: t('sidebar:menu.bonus'), icon: <CardGiftcard />, path: '/admin/transactions/bonus' },
    { text: t('sidebar:menu.debit'), icon: <TrendingDown />, path: '/admin/transactions/debit' },
    { text: t('sidebar:menu.invoice'), icon: <Receipt />, path: '/admin/transactions/invoice' },
  ]
});

const getRmbMenu = (t) => ({
  text: t('sidebar:menu.rmb'),
  icon: <CurrencyExchange />,
  submenus: [
    { text: t('sidebar:menu.exchangeRate'), icon: <CurrencyExchange />, path: '/admin/rmb/exchange-rate' },
    { text: t('sidebar:menu.history'), icon: <HistoryIcon />, path: '/admin/rmb/history' },
    { text: t('sidebar:menu.rmbTransactions'), icon: <Receipt />, path: '/admin/rmb/transactions' },
  ]
});

const getInwardTransactionsMenu = () => ({
  text: 'Inward Transactions',
  icon: <AccountBalance />,
  path: '/admin/transactions/inward',
});

const getAnalyticsMenu = (t) => ({
  text: t('sidebar:menu.analytics'),
  icon: <ShowChart />,
  submenus: [
    { text: t('sidebar:menu.orderProfit'), icon: <ShowChart />, path: '/admin/profit-analytics' },
    { text: t('sidebar:menu.rmbTransactionProfit'), icon: <CurrencyExchange />, path: '/admin/rmb-profit-analytics' },
    { text: t('sidebar:menu.userActivity') || 'User activity (DAU/WAU/MAU)', icon: <Groups />, path: '/admin/analytics/user-activity' },
  ],
});

const getMarketingMenu = (t) => ({
  text: t('sidebar:menu.marketing'),
  icon: <Campaign />,
  submenus: [
    { text: 'Manual Claim', icon: <AssignmentTurnedIn />, path: '/admin/marketing/manual-claim' },
    { text: 'Claim Customer', icon: <AssignmentInd />, path: '/admin/marketing/claim' },
    { text: t('sidebar:menu.managers'), icon: <People />, path: '/admin/marketing/managers' },
    { text: t('sidebar:menu.customerAssignments'), icon: <Assignment />, path: '/admin/marketing/assignments' },
    { text: 'My Assignments', icon: <Assignment />, path: '/admin/marketing/my-assignments' },
    { text: t('sidebar:menu.commissionTiers'), icon: <AccountTree />, path: '/admin/marketing/commission-tiers' },
    { text: t('sidebar:menu.commissions'), icon: <AttachMoneyIcon />, path: '/admin/marketing/commissions' },
    { text: t('sidebar:menu.performance'), icon: <BarChart />, path: '/admin/marketing/performance' },
    { text: 'Website Analytics', icon: <BarChart />, path: '/admin/marketing/website-analytics' },
    { text: 'Attribution', icon: <Timeline />, path: '/admin/marketing/attribution' },
  ],
});

const getEmployeeMenu = (t) => ({
  text: t('sidebar:menu.employeeManagement'),
  icon: <BusinessCenter />,
  submenus: [
    { text: t('sidebar:menu.employees'), icon: <People />, path: '/admin/employees' },
    { text: t('sidebar:menu.departments'), icon: <Folder />, path: '/admin/employees/departments' },
    { text: t('sidebar:menu.positions'), icon: <Work />, path: '/admin/employees/positions' },
    { text: t('sidebar:menu.leaveTypes'), icon: <CalendarToday />, path: '/admin/employees/leave-types' },
    { text: t('sidebar:menu.leaveRequests'), icon: <Assignment />, path: '/admin/employees/leave-requests' },
    { text: t('sidebar:menu.attendance'), icon: <BadgeIcon />, path: '/admin/employees/attendance' },
    { text: t('sidebar:menu.documents'), icon: <Description />, path: '/admin/employees/documents' },
    { text: t('sidebar:menu.dashboard'), icon: <Dashboard />, path: '/admin/employees/dashboard' },
  ],
});

const getSalaryMenu = (t) => ({
  text: t('sidebar:menu.salaryManagement'),
  icon: <AttachMoneyIcon />,
  submenus: [
    { text: t('sidebar:menu.salaryStructures'), icon: <AccountBalanceIcon />, path: '/admin/employees/salary/structures' },
    { text: t('sidebar:menu.taxConfigurations'), icon: <GavelOutlined />, path: '/admin/employees/salary/tax/configurations' },
    { text: t('sidebar:menu.taxBrackets'), icon: <AccountTree />, path: '/admin/employees/salary/tax/brackets' },
    { text: t('sidebar:menu.benefitTypes'), icon: <CardGiftcard />, path: '/admin/employees/salary/benefits/types' },
    { text: t('sidebar:menu.employeeBenefits'), icon: <CreditCard />, path: '/admin/employees/salary/benefits/employee-benefits' },
    { text: t('sidebar:menu.bonuses'), icon: <TrendingUpIcon />, path: '/admin/employees/salary/bonuses' },
    { text: t('sidebar:menu.commissions'), icon: <AttachMoneyIcon />, path: '/admin/employees/salary/commissions' },
  ],
});

const getAffiliateMenu = (t) => ({
  text: t('sidebar:menu.affiliate'),
  icon: <Handshake />,
  submenus: [
    { text: t('sidebar:menu.dashboard'), icon: <Dashboard />, path: '/admin/affiliate' },
    { text: t('sidebar:menu.list'), icon: <ListAlt />, path: '/admin/affiliate/list' },
    { text: t('sidebar:menu.rankings'), icon: <BarChart />, path: '/admin/affiliate/rankings' },
    { text: t('sidebar:menu.referrals'), icon: <People />, path: '/admin/affiliate/referrals' },
    { text: 'Manual Link', icon: <AssignmentInd />, path: '/admin/affiliate/manual-link' },
  ],
});

const getGroupBuyMenu = (t) => ({
  text: t('sidebar:menu.groupBuy'),
  icon: <ShoppingCart />,
  submenus: [
    { text: 'Active', icon: <PlayArrow />, path: '/admin/group-buy/active' },
    { text: 'Closed', icon: <Stop />, path: '/admin/group-buy/closed' },
    { text: 'Purchased', icon: <CheckCircle />, path: '/admin/group-buy/purchased' },
    { text: 'In Warehouse', icon: <Warehouse />, path: '/admin/group-buy/in-warehouse' },
    { text: 'In Transit', icon: <LocalShippingIcon />, path: '/admin/group-buy/in-transit' },
    { text: 'In Nigeria', icon: <Flag />, path: '/admin/group-buy/in-nigeria' },
    { text: 'Fulfilled', icon: <DoneAll />, path: '/admin/group-buy/fulfilled' },
    { text: 'Cancelled', icon: <Cancel />, path: '/admin/group-buy/cancelled' },
    { text: t('sidebar:menu.create'), icon: <AddShoppingCart />, path: '/admin/group-buy/create' },
    { text: t('sidebar:menu.analytics'), icon: <Analytics />, path: '/admin/group-buy/analytics' },
    { text: t('sidebar:menu.settings'), icon: <Settings />, path: '/admin/group-buy/settings' },
  ],
});

const getGroupShippingMenu = (t) => ({
  text: t('sidebar:menu.groupShipping'),
  icon: <Groups />,
  submenus: [
    { text: t('sidebar:menu.pools') || 'Pools', icon: <Groups />, path: '/admin/group-shipping' },
    { text: t('sidebar:menu.settings'), icon: <Settings />, path: '/admin/group-shipping/settings' },
  ],
});

const getExpensesMenu = (t) => ({
  text: 'Expenses',
  icon: <AttachMoneyIcon />,
  submenus: [
    { text: 'Expense List', icon: <ListAlt />, path: '/admin/expenses' },
    { text: 'Create Expense', icon: <AddCircle />, path: '/admin/expenses/create' },
    { text: 'Accounting Periods', icon: <CalendarToday />, path: '/admin/expenses/accounting-periods' },
  ],
});

const getAIMenu = (t) => ({
  text: t('sidebar:menu.ai'),
  icon: <Psychology />,
  submenus: [
    { text: t('sidebar:menu.faqManagement'), icon: <Psychology />, path: '/admin/ai/faq' },
  ],
});

const getFulfillmentMenu = (t) => ({
  text: t('sidebar:menu.fulfillment'),
  icon: <LocalShippingIcon />,
  submenus: [
    { text: t('sidebar:menu.serviceTypes'), icon: <Inventory2 />, path: '/admin/fulfillment/service-types' },
    { text: t('sidebar:menu.pricing'), icon: <AttachMoney />, path: '/admin/fulfillment/pricing' },
    { text: t('sidebar:menu.addons'), icon: <AddCircle />, path: '/admin/fulfillment/addons' },
    { text: t('sidebar:menu.storage'), icon: <Storage />, path: '/admin/fulfillment/storage' },
    { text: t('sidebar:menu.warehouseAddress'), icon: <Place />, path: '/admin/fulfillment/warehouse-address' },
    { text: t('sidebar:menu.requests'), icon: <Assignment />, path: '/admin/fulfillment/requests' },
    { text: 'Pickup Codes', icon: <VpnKey />, path: '/admin/fulfillment/pickup-codes' },
  ],
});

const getMerchantAdminMenu = () => ({
  text: 'Merchant Admin',
  icon: <Store />,
  submenus: [
    { text: 'Products', icon: <Inventory2 />, path: '/admin/products' },
    { text: 'Categories', icon: <Category />, path: '/admin/categories' },
    { text: 'Brands', icon: <BrandingWatermark />, path: '/admin/brands' },
    { text: 'Brand Requests', icon: <RequestPage />, path: '/admin/brand-requests' },
    { text: 'Schema', icon: <Schema />, path: '/admin/schema' },
    { text: 'Merchants', icon: <Store />, path: '/admin/merchants' },
    { text: 'Warehouses', icon: <Warehouse />, path: '/admin/merchant-admin/warehouses' },
    { text: 'Warehouse Rates', icon: <RequestPage />, path: '/admin/merchant-admin/rate-cards' },
    { text: 'Storage Accounts', icon: <Store />, path: '/admin/merchant-admin/storage-accounts' },
    { text: 'Inbound Shipments', icon: <LocalShipping />, path: '/admin/merchant-admin/inbound-shipments' },
    { text: 'Inventory', icon: <Inventory />, path: '/admin/merchant-admin/inventory' },
    { text: 'Warehouse Charges', icon: <RequestPage />, path: '/admin/merchant-admin/charges' },
    { text: 'Warehouse Invoices', icon: <RequestPage />, path: '/admin/merchant-admin/invoices' },
    { text: 'Marketplace Fulfillment', icon: <Inventory2 />, path: '/admin/merchant-admin/fulfillments' },
    { text: 'Warehouse Lifecycle', icon: <GavelOutlined />, path: '/admin/merchant-admin/warehouse-lifecycle' },
    { text: 'Moderation', icon: <GavelOutlined />, path: '/admin/moderation' },
    { text: 'Model Management', icon: <Psychology />, path: '/admin/moderation/model-management' },
    { text: 'Moderation Config', icon: <Settings />, path: '/admin/moderation/config' },
    { text: 'Auto Approval', icon: <Psychology />, path: '/admin/moderation/auto-approval-settings' },
  ],
});

const Sidebar = ({ sidebarCollapsed, handleSidebarToggle, navigate, location, currentPage }) => {
  const { t } = useTranslation(['sidebar', 'common']);
  const dispatch = useDispatch();
  const [locationOpen, setLocationOpen] = React.useState(false);
  const [rbacOpen, setRbacOpen] = React.useState(false);
  const [ordersOpen, setOrdersOpen] = React.useState(false);
  const [preOrderOpen, setPreOrderOpen] = React.useState(false);
  const [transactionsOpen, setTransactionsOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [apiOpen, setApiOpen] = React.useState(false);
  const [rmbOpen, setRmbOpen] = React.useState(false);
  const [analyticsOpen, setAnalyticsOpen] = React.useState(false);
  const [marketingOpen, setMarketingOpen] = React.useState(false);
  const [employeeOpen, setEmployeeOpen] = React.useState(false);
  const [salaryOpen, setSalaryOpen] = React.useState(false);
  const [affiliateOpen, setAffiliateOpen] = React.useState(false);
  const [groupBuyOpen, setGroupBuyOpen] = React.useState(false);
  const [groupShippingOpen, setGroupShippingOpen] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);
  const [fulfillmentOpen, setFulfillmentOpen] = React.useState(false);
  const [expensesOpen, setExpensesOpen] = React.useState(false);
  const [paymentMethodsOpen, setPaymentMethodsOpen] = React.useState(false);
  const [merchantAdminOpen, setMerchantAdminOpen] = React.useState(false);
  const [statusCountsLoading, setStatusCountsLoading] = React.useState(false);
  const [openDisputesCount, setOpenDisputesCount] = React.useState(0);
  const [disputesCountLoading, setDisputesCountLoading] = React.useState(false);
  const [pendingReviewsCount, setPendingReviewsCount] = React.useState(0);
  const [pendingWithdrawalsCount, setPendingWithdrawalsCount] = React.useState(0);
  const [pendingRmbTransactionsCount, setPendingRmbTransactionsCount] = React.useState(0);
  const user = useSelector(state => state.auth.user);
  const { statusCounts } = useSelector(state => state.order);
  const { unreadCount: supportChatUnreadCount } = useSupportChatNotifications();

  // Get translated menu items
  const rbacMenu = getRbacMenu(t);
  const walletMenu = getWalletMenu(t);
  const walletsMenu = getWalletsMenu(t);
  const bonusManagementMenu = getBonusManagementMenu(t);
  const supportChatMenu = getSupportChatMenu();
  const supportAgentRatingsMenu = getSupportAgentRatingsMenu();
  const supportKnowledgeMenu = getSupportKnowledgeMenu();
  const reviewsMenu = getReviewsMenu();
  const paymentMethodsMenu = getPaymentMethodsMenu(t);
  const withdrawalsMenu = getWithdrawalsMenu(t);
  const locationMenu = getLocationMenu(t);
  const searchMenu = getSearchMenu(t);
  const apiMenu = getApiMenu(t);
  const ordersMenu = getOrdersMenu(t);
  const preOrderMenu = getPreOrderMenu(t);
  const transactionsMenu = getTransactionsMenu(t);
  const inwardTransactionsMenu = getInwardTransactionsMenu();
  const rmbMenu = getRmbMenu(t);
  const analyticsMenu = getAnalyticsMenu(t);
  const marketingMenu = getMarketingMenu(t);
  const employeeMenu = getEmployeeMenu(t);
  const salaryMenu = getSalaryMenu(t);
  const affiliateMenu = getAffiliateMenu(t);
  const groupBuyMenu = getGroupBuyMenu(t);
  const groupShippingMenu = getGroupShippingMenu(t);
  const expensesMenu = getExpensesMenu(t);
  const aiMenu = getAIMenu(t);
  const fulfillmentMenu = getFulfillmentMenu(t);
  const merchantAdminMenu = getMerchantAdminMenu();

  const canAccessRoute = useCallback((path) => {
    if (!path) return true;
    if (!user) return false;
    if (user.is_superuser) return true;
    const required = getRoutePermissions(path);
    if (!required.length) return true;
    return hasAnyEndpointAccess(user, required);
  }, [user]);

  React.useEffect(() => {
    if (!canAccessRoute('/admin/withdrawals')) {
      setPendingWithdrawalsCount(0);
      return undefined;
    }
    let active = true;
    let loading = false;
    const refresh = async () => {
      if (loading) return;
      loading = true;
      try {
        const response = await fetchWithdrawalRequests({ status: 'pending', page: 1, page_size: 1 });
        if (active) setPendingWithdrawalsCount(Number(response?.count ?? (Array.isArray(response) ? response.length : 0)));
      } catch (error) {
        console.error('Failed to refresh pending withdrawal count:', error);
      } finally {
        loading = false;
      }
    };
    refresh();
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    window.addEventListener('withdrawals:pending-count-refresh', refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('withdrawals:pending-count-refresh', refresh);
    };
  }, [canAccessRoute]);

  React.useEffect(() => {
    if (!canAccessRoute('/admin/rmb/transactions')) {
      setPendingRmbTransactionsCount(0);
      return undefined;
    }

    let active = true;
    let loading = false;
    const refresh = async () => {
      if (loading) return;
      loading = true;
      try {
        const response = await getBuyRMBTransactions({ status: 'pending', page: 1, page_size: 1 });
        const count = response?.count ?? response?.total ?? (Array.isArray(response) ? response.length : 0);
        if (active) setPendingRmbTransactionsCount(Number(count || 0));
      } catch (error) {
        console.error('Failed to refresh pending RMB transaction count:', error);
      } finally {
        loading = false;
      }
    };

    refresh();
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    window.addEventListener('rmb-transactions:pending-count-refresh', refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('rmb-transactions:pending-count-refresh', refresh);
    };
  }, [canAccessRoute]);

  const refreshPendingReviewsCount = useCallback(() => {
    if (!canAccessRoute('/admin/reviews')) {
      setPendingReviewsCount(0);
      return Promise.resolve();
    }

    return getPendingSupportReviewsCount()
      .then((count) => setPendingReviewsCount(Number(count || 0)))
      .catch((error) => {
        console.error('Error fetching pending reviews count:', error);
        setPendingReviewsCount(0);
      });
  }, [canAccessRoute]);

  // Fetch initial badge counts
  React.useEffect(() => {
    if (canAccessRoute('/admin/orders/all')) {
      dispatch(fetchOrderStatusCounts());
    }
  }, [dispatch, canAccessRoute]);

  // Fetch status counts when Orders menu is expanded
  React.useEffect(() => {
    if (ordersOpen && canAccessRoute('/admin/orders/all')) {
      setStatusCountsLoading(true);
      dispatch(fetchOrderStatusCounts())
        .finally(() => setStatusCountsLoading(false));
    }
  }, [ordersOpen, dispatch, canAccessRoute]);

  // Fetch open disputes count
  React.useEffect(() => {
    if (canAccessRoute('/admin/packages')) {
      setDisputesCountLoading(true);
      getOpenDisputesCount()
        .then(count => setOpenDisputesCount(count))
        .catch(error => {
          console.error('Error fetching disputes count:', error);
          setOpenDisputesCount(0);
        })
        .finally(() => setDisputesCountLoading(false));
    }
  }, [canAccessRoute]);

  React.useEffect(() => {
    refreshPendingReviewsCount();
  }, [refreshPendingReviewsCount]);

  React.useEffect(() => {
    const handleReviewsCountRefresh = () => {
      refreshPendingReviewsCount();
    };

    window.addEventListener('support-reviews:pending-count-refresh', handleReviewsCountRefresh);
    return () => {
      window.removeEventListener('support-reviews:pending-count-refresh', handleReviewsCountRefresh);
    };
  }, [refreshPendingReviewsCount]);

  // Refresh disputes count when navigating to packages page
  React.useEffect(() => {
    if (location.pathname === '/admin/packages' && canAccessRoute('/admin/packages')) {
      getOpenDisputesCount()
        .then(count => setOpenDisputesCount(count))
        .catch(error => {
          console.error('Error fetching disputes count:', error);
        });
    }
  }, [location.pathname, canAccessRoute]);

React.useEffect(() => {
  if (ordersMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setOrdersOpen(true);
  }
  if (preOrderMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setPreOrderOpen(true);
  }
  if (rbacMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setRbacOpen(true);
  }
  if (rmbMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setRmbOpen(true);
  }
  if (analyticsMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setAnalyticsOpen(true);
  }
  if (marketingMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setMarketingOpen(true);
  }
  if (employeeMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setEmployeeOpen(true);
  }
  if (salaryMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setSalaryOpen(true);
  }
  if (affiliateMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setAffiliateOpen(true);
  }
  if (groupBuyMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setGroupBuyOpen(true);
  }
  if (aiMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setAiOpen(true);
  }
  if (fulfillmentMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setFulfillmentOpen(true);
  }
  if (expensesMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setExpensesOpen(true);
  }
  if (groupShippingMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setGroupShippingOpen(true);
  }
  if (merchantAdminMenu.submenus.some((submenu) => location.pathname === submenu.path)) {
    setMerchantAdminOpen(true);
  }
}, [location.pathname, ordersMenu.submenus, preOrderMenu.submenus, affiliateMenu.submenus, aiMenu.submenus, analyticsMenu.submenus, employeeMenu.submenus, fulfillmentMenu.submenus, marketingMenu.submenus, rbacMenu.submenus, rmbMenu.submenus, salaryMenu.submenus, expensesMenu.submenus, groupBuyMenu.submenus, groupShippingMenu.submenus, merchantAdminMenu.submenus]);

  const handleLocationClick = () => {
    setLocationOpen((prev) => !prev);
  };

  const handleSearchClick = () => {
    setSearchOpen((prev) => !prev);
  };

  const handleApiClick = () => {
    setApiOpen((prev) => !prev);
  };

  const handleRmbClick = () => {
    setRmbOpen((prev) => !prev);
  };

  const handleAnalyticsClick = () => {
    setAnalyticsOpen((prev) => !prev);
  };

  const handleMarketingClick = () => {
    setMarketingOpen((prev) => !prev);
  };

  const handleEmployeeClick = () => {
    setEmployeeOpen((prev) => !prev);
  };

  const handleSalaryClick = () => {
    setSalaryOpen((prev) => !prev);
  };

  const handleAffiliateClick = () => {
    setAffiliateOpen((prev) => !prev);
  };

  const handleAIClick = () => {
    setAiOpen((prev) => !prev);
  };

  const handleFulfillmentClick = () => {
    setFulfillmentOpen((prev) => !prev);
  };

  const handleExpensesClick = () => {
    setExpensesOpen((prev) => !prev);
  };

  const handleMerchantAdminClick = () => {
    setMerchantAdminOpen((prev) => !prev);
  };

  return (
    <Box>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
        {!sidebarCollapsed && (
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(45deg, #DC143C, #FF6B6B)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>C</Typography>
          </Box>
        )}
        {!sidebarCollapsed && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>{t('appName')}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('appSubtitle')}</Typography>
          </Box>
        )}
        {sidebarCollapsed && (
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(45deg, #DC143C, #FF6B6B)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>C</Typography>
          </Box>
        )}
      </Box>
      <Divider />
      <List>
        {/* Render all main menu items alphabetically */}
        {getMenuItems(t).map((item) => {
          if (!canAccessRoute(item.path)) {
            return null;
          }
          return (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={sidebarCollapsed ? item.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path && currentPage === 'main'}
                sx={{ mx: 1, borderRadius: 1, minHeight: 48, justifyContent: sidebarCollapsed ? 'center' : 'flex-start', '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path && currentPage === 'main' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {item.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        );
        })}

        {rbacMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? rbacMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={() => setRbacOpen((prev) => !prev)}
                  selected={rbacOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: rbacMenu.submenus.some((submenu) => location.pathname === submenu.path) ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {rbacMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={rbacMenu.text} />}
                  {!sidebarCollapsed && (rbacOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && rbacOpen && rbacMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {canAccessRoute(walletMenu.path) && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? walletMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(walletMenu.path)}
                selected={location.pathname === walletMenu.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === walletMenu.path ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {walletMenu.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={walletMenu.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {canAccessRoute(walletsMenu.path) && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? walletsMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(walletsMenu.path)}
                selected={location.pathname === walletsMenu.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === walletsMenu.path ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {walletsMenu.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={walletsMenu.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {canAccessRoute(bonusManagementMenu.path) && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? bonusManagementMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(bonusManagementMenu.path)}
                selected={location.pathname === bonusManagementMenu.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === bonusManagementMenu.path ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {bonusManagementMenu.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={bonusManagementMenu.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {canAccessRoute(supportChatMenu.path) && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? supportChatMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(supportChatMenu.path)}
                selected={location.pathname === supportChatMenu.path || location.pathname.startsWith('/admin/support-chat/')}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === supportChatMenu.path || location.pathname.startsWith('/admin/support-chat/') ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {supportChatMenu.icon}
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText
                    primary={
                      supportChatUnreadCount > 0 ? (
                        <Badge badgeContent={supportChatUnreadCount} color="error" sx={{ '& .MuiBadge-badge': { right: -18, top: 8 } }}>
                          {supportChatMenu.text}
                        </Badge>
                      ) : (
                        supportChatMenu.text
                      )
                    }
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {canAccessRoute(supportAgentRatingsMenu.path) && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? supportAgentRatingsMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(supportAgentRatingsMenu.path)}
                selected={location.pathname === supportAgentRatingsMenu.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === supportAgentRatingsMenu.path ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {supportAgentRatingsMenu.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={supportAgentRatingsMenu.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {canAccessRoute(supportKnowledgeMenu.path) && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? supportKnowledgeMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(supportKnowledgeMenu.path)}
                selected={location.pathname === supportKnowledgeMenu.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === supportKnowledgeMenu.path ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {supportKnowledgeMenu.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={supportKnowledgeMenu.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {canAccessRoute(reviewsMenu.path) && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? reviewsMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(reviewsMenu.path)}
                selected={location.pathname === reviewsMenu.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === reviewsMenu.path ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {sidebarCollapsed && pendingReviewsCount > 0 ? (
                    <Badge badgeContent={pendingReviewsCount} color="error">
                      {reviewsMenu.icon}
                    </Badge>
                  ) : (
                    reviewsMenu.icon
                  )}
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText
                    primary={
                      pendingReviewsCount > 0 ? (
                        <Badge badgeContent={pendingReviewsCount} color="error" sx={{ '& .MuiBadge-badge': { right: -18, top: 8 } }}>
                          {reviewsMenu.text}
                        </Badge>
                      ) : (
                        reviewsMenu.text
                      )
                    }
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {merchantAdminMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? merchantAdminMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleMerchantAdminClick}
                  selected={merchantAdminOpen || merchantAdminMenu.submenus.some((submenu) => location.pathname === submenu.path)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: merchantAdminMenu.submenus.some((submenu) => location.pathname === submenu.path) ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {merchantAdminMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={merchantAdminMenu.text} />}
                  {!sidebarCollapsed && (merchantAdminOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && merchantAdminOpen && merchantAdminMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {canAccessRoute('/admin/invoice-management') && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('menu.invoiceManagement') : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate('/admin/invoice-management')}
                selected={location.pathname === '/admin/invoice-management'}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === '/admin/invoice-management' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <Receipt />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('menu.invoiceManagement')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {paymentMethodsMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? paymentMethodsMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={() => setPaymentMethodsOpen((prev) => !prev)}
                  selected={paymentMethodsOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: paymentMethodsMenu.submenus.some((submenu) => location.pathname === submenu.path) ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {paymentMethodsMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={paymentMethodsMenu.text} />}
                  {!sidebarCollapsed && (paymentMethodsOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && paymentMethodsOpen && paymentMethodsMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {canAccessRoute(withdrawalsMenu.path) && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? withdrawalsMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(withdrawalsMenu.path)}
                selected={location.pathname === withdrawalsMenu.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === withdrawalsMenu.path ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <Badge badgeContent={pendingWithdrawalsCount} color="error">{withdrawalsMenu.icon}</Badge>
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={withdrawalsMenu.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {/* Insert Orders expandable menu here (red circled) */}
        {ordersMenu && ordersMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? ordersMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={() => setOrdersOpen((prev) => !prev)}
                  selected={ordersOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: ordersOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {ordersMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={ordersMenu.text} />}
                  {!sidebarCollapsed && (ordersOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {/* Orders submenus */}
            {!sidebarCollapsed && ordersOpen && ordersMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => {
              const trackedKey = TRACKED_STATUS_PATHS[submenu.path];
              const badgeCount = trackedKey ? statusCounts[trackedKey] : undefined;
              return (
                <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                  <Tooltip title={submenu.text} placement="right" disableHoverListener>
                    <ListItemButton
                      onClick={() => navigate(submenu.path)}
                      selected={location.pathname === submenu.path}
                      sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                    >
                      <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                        {submenu.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          trackedKey && statusCountsLoading ? (
                            <Skeleton variant="rectangular" width={40} height={24} sx={{ display: 'inline-block', borderRadius: 2 }} />
                          ) : badgeCount !== undefined ? (
                            <Badge badgeContent={badgeCount} color="error" sx={{ '& .MuiBadge-badge': { right: -18, top: 8 } }}>
                              {submenu.text}
                            </Badge>
                          ) : submenu.text
                        }
                      />
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </>
        )}

        {preOrderMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? preOrderMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={() => setPreOrderOpen((prev) => !prev)}
                  selected={preOrderOpen || preOrderMenu.submenus.some((submenu) => location.pathname === submenu.path)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: preOrderOpen || preOrderMenu.submenus.some((submenu) => location.pathname === submenu.path) ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {preOrderMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={preOrderMenu.text} />}
                  {!sidebarCollapsed && (preOrderOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && preOrderOpen && preOrderMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}
        
        {/* Packages Menu - directly below Orders */}
        {canAccessRoute('/admin/packages') && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('menu.packages') : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate('/admin/packages')}
                selected={location.pathname === '/admin/packages'}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === '/admin/packages' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <Inventory2 />
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText
                    primary={
                      disputesCountLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {t('menu.packages')}
                          <Skeleton variant="circular" width={20} height={20} />
                        </Box>
                      ) : openDisputesCount > 0 ? (
                        <Badge badgeContent={openDisputesCount} color="error" sx={{ '& .MuiBadge-badge': { right: -18, top: 8 } }}>
                          {t('menu.packages')}
                        </Badge>
                      ) : (
                        t('menu.packages')
                      )
                    }
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
        {canAccessRoute('/admin/packages/assisted') && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('menu.assistedPackages') : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate('/admin/packages/assisted')}
                selected={location.pathname === '/admin/packages/assisted'}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === '/admin/packages/assisted' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <Inventory2 />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('menu.assistedPackages')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {/* Shipment Management Menu */}
        {canAccessRoute('/admin/shipments') && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? 'Shipments' : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate('/admin/shipments')}
                selected={location.pathname.startsWith('/admin/shipments')}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname.startsWith('/admin/shipments') ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <LocalShippingIcon />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary="Shipments" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
        
        {/* Packaging Materials Menu */}
        {canAccessRoute('/admin/packaging-materials') && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? 'Packaging Materials' : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate('/admin/packaging-materials')}
                selected={location.pathname === '/admin/packaging-materials'}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === '/admin/packaging-materials' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <Inventory2 />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary="Packaging Materials" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {/* Logistics Menu - below Packages */}
        {canAccessRoute('/admin/logistics') && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('menu.logistics') : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate('/admin/logistics')}
                selected={location.pathname === '/admin/logistics'}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === '/admin/logistics' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <LocalShippingIcon />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('menu.logistics')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
        {canAccessRoute('/admin/logistics/info') && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('menu.logisticsInfo') : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate('/admin/logistics/info')}
                selected={location.pathname === '/admin/logistics/info'}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === '/admin/logistics/info' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <Calculate />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('menu.logisticsInfo')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {/* Group Shipping Menu - expandable */}
        {groupShippingMenu && groupShippingMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? groupShippingMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={() => setGroupShippingOpen((prev) => !prev)}
                  selected={groupShippingOpen || location.pathname.startsWith('/admin/group-shipping')}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: (groupShippingOpen || location.pathname.startsWith('/admin/group-shipping')) ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {groupShippingMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={groupShippingMenu.text} />}
                  {!sidebarCollapsed && (groupShippingOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && groupShippingOpen && groupShippingMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      minHeight: 40,
                      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                      '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                    }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    {!sidebarCollapsed && <ListItemText primary={submenu.text} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* Group Buy Menu - expandable */}
        {groupBuyMenu && groupBuyMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? groupBuyMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={() => setGroupBuyOpen((prev) => !prev)}
                  selected={groupBuyOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: groupBuyOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {groupBuyMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={groupBuyMenu.text} />}
                  {!sidebarCollapsed && (groupBuyOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && groupBuyOpen && groupBuyMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      minHeight: 40,
                      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: sidebarCollapsed ? 'auto' : 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    {!sidebarCollapsed && <ListItemText primary={submenu.text} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* Insert Transactions expandable menu here */}
        {transactionsMenu && transactionsMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? transactionsMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={() => setTransactionsOpen((prev) => !prev)}
                  selected={transactionsOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: transactionsOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {transactionsMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={transactionsMenu.text} />}
                  {!sidebarCollapsed && (transactionsOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {/* Transactions submenus */}
            {!sidebarCollapsed && transactionsOpen && transactionsMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 40 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}
        {canAccessRoute(inwardTransactionsMenu.path) && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? inwardTransactionsMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(inwardTransactionsMenu.path)}
                selected={location.pathname === inwardTransactionsMenu.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === inwardTransactionsMenu.path ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {inwardTransactionsMenu.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={inwardTransactionsMenu.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
        {/* Render the rest of menuItems after Merchants (skip Orders); exclude banners to avoid duplicate */}
        {getMenuItems(t).slice(4).filter((item) => item.path !== '/admin/banners').map((item) => {
          if (!canAccessRoute(item.path)) {
            return null;
          }
          return (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={sidebarCollapsed ? item.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path && currentPage === 'main'}
                sx={{ mx: 1, borderRadius: 1, minHeight: 48, justifyContent: sidebarCollapsed ? 'center' : 'flex-start', '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path && currentPage === 'main' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  {item.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        );
        })}
        
        {/* Affiliate expandable menu */}
        {affiliateMenu && affiliateMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? affiliateMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleAffiliateClick}
                  selected={affiliateOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: affiliateOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {affiliateMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={affiliateMenu.text} />}
                  {!sidebarCollapsed && (affiliateOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && affiliateOpen && affiliateMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}
        {/* AI expandable menu */}
        {aiMenu && aiMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? aiMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleAIClick}
                  selected={aiOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: aiOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {aiMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={aiMenu.text} />}
                  {!sidebarCollapsed && (aiOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && aiOpen && aiMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {fulfillmentMenu && fulfillmentMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? fulfillmentMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleFulfillmentClick}
                  selected={fulfillmentOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: fulfillmentOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {fulfillmentMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={fulfillmentMenu.text} />}
                  {!sidebarCollapsed && (fulfillmentOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && fulfillmentOpen && fulfillmentMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        
        {/* Search expandable menu */}
        {searchMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? searchMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleSearchClick}
                  selected={searchOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: searchOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {searchMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={searchMenu.text} />}
                  {!sidebarCollapsed && (searchOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {/* Search submenus */}
            {!sidebarCollapsed && searchOpen && searchMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* API expandable menu */}
        {apiMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? apiMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleApiClick}
                  selected={apiOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: apiOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {apiMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={apiMenu.text} />}
                  {!sidebarCollapsed && (apiOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {/* API submenus */}
            {!sidebarCollapsed && apiOpen && apiMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* RMB expandable menu */}
        {rmbMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? rmbMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleRmbClick}
                  selected={rmbOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: rmbOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    <Badge badgeContent={pendingRmbTransactionsCount} color="error">
                      {rmbMenu.icon}
                    </Badge>
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={rmbMenu.text} />}
                  {!sidebarCollapsed && (rmbOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {/* RMB submenus */}
            {!sidebarCollapsed && rmbOpen && rmbMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={submenu.path === '/admin/rmb/transactions' ? (
                        <Badge badgeContent={pendingRmbTransactionsCount} color="error" sx={{ '& .MuiBadge-badge': { right: -18, top: 8 } }}>
                          {submenu.text}
                        </Badge>
                      ) : submenu.text}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* Location expandable menu */}
        {locationMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? locationMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleLocationClick}
                  selected={locationOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: locationOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {locationMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={locationMenu.text} />}
                  {!sidebarCollapsed && (locationOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {/* Location submenus */}
            {!sidebarCollapsed && locationOpen && locationMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* Analytics Menu */}
        {analyticsMenu.submenus.some((submenu) => canAccessRoute(submenu.path)) && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? analyticsMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleAnalyticsClick}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&:hover': { backgroundColor: 'rgba(220, 20, 60, 0.1)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {analyticsMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={analyticsMenu.text} />}
                  {!sidebarCollapsed && (analyticsOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && analyticsOpen && analyticsMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* Marketing Menu */}
        {marketingMenu.submenus.some((submenu) => canAccessRoute(submenu.path)) && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? marketingMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleMarketingClick}
                  selected={marketingOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: marketingOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {marketingMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={marketingMenu.text} />}
                  {!sidebarCollapsed && (marketingOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && marketingOpen && marketingMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* Employee Management Menu */}
        {employeeMenu.submenus.some((submenu) => canAccessRoute(submenu.path)) && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? employeeMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleEmployeeClick}
                  selected={employeeOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: employeeOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {employeeMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={employeeMenu.text} />}
                  {!sidebarCollapsed && (employeeOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && employeeOpen && employeeMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* Salary Management Menu */}
        {salaryMenu.submenus.some((submenu) => canAccessRoute(submenu.path)) && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? salaryMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleSalaryClick}
                  selected={salaryOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: salaryOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {salaryMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={salaryMenu.text} />}
                  {!sidebarCollapsed && (salaryOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && salaryOpen && salaryMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* Expenses Menu */}
        {expensesMenu && expensesMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).length > 0 && (
          <>
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? expensesMenu.text : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
                <ListItemButton
                  onClick={handleExpensesClick}
                  selected={expensesOpen}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                  }}
                >
                  <ListItemIcon sx={{ color: expensesOpen ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                    {expensesMenu.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={expensesMenu.text} />}
                  {!sidebarCollapsed && (expensesOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {!sidebarCollapsed && expensesOpen && expensesMenu.submenus.filter((submenu) => canAccessRoute(submenu.path)).map((submenu) => (
              <ListItem key={submenu.text} disablePadding sx={{ pl: 4 }}>
                <Tooltip title={submenu.text} placement="right" disableHoverListener>
                  <ListItemButton
                    onClick={() => navigate(submenu.path)}
                    selected={location.pathname === submenu.path}
                    sx={{ mx: 1, borderRadius: 1, minHeight: 40, '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } } }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === submenu.path ? 'white' : 'inherit', minWidth: 32 }}>
                      {submenu.icon}
                    </ListItemIcon>
                    <ListItemText primary={submenu.text} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}

        {/* Bank Menu */}
        {canAccessRoute('/admin/banks') && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('menu.bank') : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate('/admin/banks')}
                selected={location.pathname === '/admin/banks'}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === '/admin/banks' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <AccountBalanceIcon />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('menu.bank')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {/* Virtual Account Management (Wema) */}
        {canAccessRoute('/admin/virtual-accounts') && (
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? 'Virtual Accounts' : ''} placement="right" disableHoverListener={!sidebarCollapsed}>
              <ListItemButton
                onClick={() => navigate('/admin/virtual-accounts')}
                selected={location.pathname === '/admin/virtual-accounts'}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': { backgroundColor: '#DC143C', color: 'white', '&:hover': { backgroundColor: '#B91C3C' } }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === '/admin/virtual-accounts' ? 'white' : 'inherit', minWidth: sidebarCollapsed ? 'auto' : 40 }}>
                  <LocalAtm />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary="Virtual Accounts" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
      </List>
      <Divider />
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
        <IconButton onClick={handleSidebarToggle} size="small">
          {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default Sidebar;
